import { supabase } from '@/integrations/supabase/client';

/**
 * Security event types for audit logging
 */
export type SecurityEventType = 
  | 'access_denied' 
  | 'permission_check_failed' 
  | 'bulk_operation' 
  | 'sensitive_data_access' 
  | 'settings_change'
  | 'role_assignment'
  | 'role_removal'
  | 'user_creation'
  | 'user_deletion';

export type SecuritySeverity = 'low' | 'medium' | 'high' | 'critical';

interface SecurityEvent {
  event_type: SecurityEventType;
  severity: SecuritySeverity;
  resource: string;
  action: string;
  metadata?: Record<string, any>;
}

/**
 * Log security events for audit trail
 * Creates comprehensive logs for security-relevant operations
 */
export async function logSecurityEvent(event: SecurityEvent): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    // Prepare metadata with enriched context
    const enrichedMetadata = {
      ...event.metadata,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      url: window.location.href,
      referrer: document.referrer,
    };

    // Log to activity logs via edge function
    await supabase.functions.invoke('log-activity', {
      body: {
        entity_type: 'security_event',
        entity_id: crypto.randomUUID(),
        action: event.event_type,
        user_id: user?.id || null,
        metadata: {
          severity: event.severity,
          resource: event.resource,
          action_performed: event.action,
          event_details: enrichedMetadata,
        },
      },
    });

    // For high/critical severity, also log to console for immediate visibility
    if (event.severity === 'high' || event.severity === 'critical') {
      console.warn(`[SECURITY ${event.severity.toUpperCase()}]`, {
        type: event.event_type,
        resource: event.resource,
        action: event.action,
        user: user?.email || 'anonymous',
      });
    }
  } catch (error) {
    // Fallback: log to console if edge function fails
    console.error('Failed to log security event:', error);
    console.warn('[SECURITY EVENT]', event);
  }
}

/**
 * Log bulk operations for audit compliance
 */
export async function logBulkOperation(params: {
  action: string;
  targetIds: string[];
  resource: string;
  metadata?: Record<string, any>;
}): Promise<void> {
  await logSecurityEvent({
    event_type: 'bulk_operation',
    severity: params.targetIds.length > 50 ? 'high' : 'medium',
    resource: params.resource,
    action: params.action,
    metadata: {
      target_count: params.targetIds.length,
      target_ids: params.targetIds,
      ...params.metadata,
    },
  });
}

/**
 * Log sensitive data access
 */
export async function logDataAccess(params: {
  resource: string;
  action: 'view' | 'export' | 'download';
  recordCount?: number;
  metadata?: Record<string, any>;
}): Promise<void> {
  await logSecurityEvent({
    event_type: 'sensitive_data_access',
    severity: params.action === 'export' ? 'high' : 'medium',
    resource: params.resource,
    action: params.action,
    metadata: {
      record_count: params.recordCount,
      ...params.metadata,
    },
  });
}

/**
 * Log permission check failures for security monitoring
 */
export async function logPermissionFailure(params: {
  resource: string;
  requiredPermission: string;
  userRoles?: string[];
  metadata?: Record<string, any>;
}): Promise<void> {
  await logSecurityEvent({
    event_type: 'permission_check_failed',
    severity: 'high',
    resource: params.resource,
    action: 'access_attempt',
    metadata: {
      required_permission: params.requiredPermission,
      user_roles: params.userRoles,
      ...params.metadata,
    },
  });
}
